import { z } from 'zod';

/**
 * Pricing reference data: zones and named points rate lines key on
 * (gitaumoses4/chukua#21). Never free-text addresses — those won't match.
 */

/**
 * What a zone/named point can be — a deployment's own vocabulary (`zone_kinds`
 * table), not a hardcoded enum: this platform serves more than one tenant, and a
 * fixed list would have baked Index Cargo's corridor into every deployment.
 * `code` is what `ZoneInput.kind` matches against.
 */
export const zoneKindInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
});
export type ZoneKindInput = z.infer<typeof zoneKindInputSchema>;

const geoJsonPositionSchema = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);

export const zoneGeoJsonPointSchema = z.object({
  type: z.literal('Point'),
  coordinates: geoJsonPositionSchema,
});
export type ZoneGeoJsonPoint = z.infer<typeof zoneGeoJsonPointSchema>;

/** RFC 7946: a polygon's linear rings must be closed — first and last position equal. A hand-built form can violate this. */
function ringIsClosed(ring: [number, number][]): boolean {
  const [first] = ring;
  const last = ring[ring.length - 1];
  return ring.length >= 4 && first[0] === last[0] && first[1] === last[1];
}

export const zoneGeoJsonPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z
    .array(z.array(geoJsonPositionSchema).min(4))
    .min(1)
    .refine((rings) => rings.every(ringIsClosed), { message: 'Each polygon ring must be closed (first and last position equal)' }),
});
export type ZoneGeoJsonPolygon = z.infer<typeof zoneGeoJsonPolygonSchema>;

export const zoneInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(160),
  /** Matched against the zone-kind catalogue's `code` at write time — see ZonesService.assertValidKind. */
  kind: z.string().trim().min(1).max(32),
  country: z.string().trim().min(1).max(80),
  polygon: zoneGeoJsonPolygonSchema.nullable().optional(),
  centroid: zoneGeoJsonPointSchema.nullable().optional(),
});
export type ZoneInput = z.infer<typeof zoneInputSchema>;
